import * as React from 'react';
import { cn } from './lib/utils';

// Maps domain name → CSS custom property for the accent bar
const DOMAIN_COLOR = {
  staffing:  'var(--yellow)',
  quality:   'var(--royal)',
  access:    'var(--aqua)',
  finance:   'var(--green)',
  consumer:  'var(--orange-warn)',
  health:    'var(--teal-dark)',
  practice:  'var(--navy-light)',
  ops:       'var(--navy-light)',
};

// Alert level → accent bar color
const ALERT_ACCENT = {
  none:     null,
  warning:  'var(--yellow)',
  critical: 'var(--red-critical)',
};

// Alert level → background tint
const ALERT_BG = {
  none:     '',
  warning:  'bg-yellow-bg',
  critical: 'bg-red-bg',
};

// Inner padding per density (left-accent gets extra pl for bar clearance)
const DENSITY_PAD = {
  compact:  { left: 'pl-3 py-1.5 pr-2', top: 'pt-2 px-2 pb-1.5' },
  default:  { left: 'pl-4 py-2.5 pr-3',  top: 'pt-2.5 px-3 pb-2.5' },
  spacious: { left: 'pl-5 py-3 pr-4',  top: 'pt-3 px-4 pb-3' },
};

/**
 * White card with a 3px accent bar coloured by domain or alert level.
 *
 * accentPlacement="left" (default) — 3px bar on the left edge
 * accentPlacement="top"            — 3px bar across the top edge
 *
 * alertLevel="none"     — domain accent color, no background tint (default)
 * alertLevel="warning"  — yellow accent bar + subtle yellow background
 * alertLevel="critical" — red accent bar + subtle red background
 *
 * Backward compatible: alert={true} is treated as alertLevel="critical".
 *
 * density="compact" | "default" | "spacious"
 *   Controls left-bar clearance (pl-*). Content padding is the caller's
 *   responsibility — pass it via className (KPICard does this).
 */
function DomainCard({
  domain,
  title,
  children,
  alert = false,
  alertLevel,
  accentPlacement = 'left',
  density = 'default',
  className,
  accentColor,
  ...props
}) {
  const level = alertLevel ?? (alert ? 'critical' : 'none');

  const accent = ALERT_ACCENT[level] !== null && ALERT_ACCENT[level] !== undefined
    ? ALERT_ACCENT[level]
    : (accentColor ?? DOMAIN_COLOR[domain] ?? 'var(--aqua)');

  const barClass = accentPlacement === 'top'
    ? 'absolute left-0 right-0 top-0 h-[3px] rounded-t-card'
    : 'absolute left-0 top-0 bottom-0 w-[3px] rounded-l-card';

  const pad = DENSITY_PAD[density] ?? DENSITY_PAD.default;
  const innerClass = accentPlacement === 'left' ? pad.left : pad.top;

  return (
    <div
      className={cn(
        'relative rounded-card bg-nn-white shadow-card overflow-hidden hover:shadow-md transition-shadow',
        ALERT_BG[level],
        className
      )}
      {...props}
    >
      <div className={barClass} style={{ background: accent }} aria-hidden="true" />
      <div className={innerClass}>
        {title && (
          <div className="px-4 pt-4 pb-2">
            <h3 className="font-heading text-heading-sm text-text-dark leading-tight">{title}</h3>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export { DomainCard, DOMAIN_COLOR };
